import { NumbersOutlined } from "@mui/icons-material";
import axios from "axios";
import { API_URL } from "config/api";

const endpoint = API_URL;

// Función para formatear números con separador de miles y centavos
const formatNumber = (number, decimals = 2) => {
  return new Intl.NumberFormat("es-MX", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(number);
};

// Función para formatear números grandes de forma compacta
const formatCompactNumber = (number) => {
  if (number >= 1000000) {
    return "$" + (number / 1000000).toFixed(2) + "M";
  } else if (number >= 10000) {
    return "$" + (number / 1000).toFixed(1) + "K";
  } else {
    return "$ " + formatNumber(number);
  }
};

export const getCount = async (companyId) => {
  try {
    const response = await axios.get(`${endpoint}/usuarios`);
    // En este mock, los usuarios no están vinculados directamente a empresas.
    // Podríamos filtrar si existiera la relación, por ahora devolvemos el total o un subconjunto.
    return formatNumber(response.data.length, 0);
  } catch (error) {
    console.error("Error fetching usuarios (count):", error.message);
    return "0";
  }
};

export const getIngresos = async (companyId) => {
  try {
    const [ventasRes, sucursalesRes] = await Promise.all([
      axios.get(`${endpoint}/ventas`),
      axios.get(`${endpoint}/sucursales`),
    ]);

    let ventas = Array.isArray(ventasRes.data) ? ventasRes.data : [];
    const sucursales = Array.isArray(sucursalesRes.data) ? sucursalesRes.data : [];

    // Filtrar ventas por empresa si hay un companyId
    if (companyId) {
      const sucursalesIds = sucursales
        .filter((s) => String(s.empresa_id) === String(companyId))
        .map((s) => s.id);
      ventas = ventas.filter((v) => sucursalesIds.includes(v.sucursal_id));
    }

    // Calcular ingresos totales del mes actual
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    const totalCurrentMonth = ventas
      .filter((venta) => {
        const fecha = new Date(venta.fecha_venta);
        return fecha >= startOfMonth && fecha <= endOfMonth;
      })
      .reduce((acc, venta) => acc + (Number(venta.total) || 0), 0);

    // Calcular ingresos del mes pasado
    const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);

    const totalLastMonth = ventas
      .filter((venta) => {
        const fecha = new Date(venta.fecha_venta);
        return fecha >= startOfLastMonth && fecha <= endOfLastMonth;
      })
      .reduce((acc, venta) => acc + (Number(venta.total) || 0), 0);

    const percentageChange =
      totalLastMonth === 0
        ? totalCurrentMonth > 0
          ? 100
          : 0
        : ((totalCurrentMonth - totalLastMonth) / totalLastMonth) * 100;

    return [formatCompactNumber(totalCurrentMonth), percentageChange.toFixed(2)];
  } catch (error) {
    console.error("Error fetching ingresos:", error.message);
    return ["$0.00", 0];
  }
};

export const getSucursales = async (companyId) => {
  try {
    const response = await axios.get(`${endpoint}/sucursales`);
    let sucursalesList = Array.isArray(response.data) ? response.data : [];

    // Filtrar por empresa si hay un companyId
    if (companyId) {
      sucursalesList = sucursalesList.filter((s) => String(s.empresa_id) === String(companyId));
    }

    const totalSucursales = sucursalesList.length;

    // Calcular el total del día anterior
    const now = new Date();
    const endOfYesterday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() - 1,
      23,
      59,
      59
    );

    const totalYesterday = sucursalesList.filter(
      (sucursal) => sucursal.updated_at && new Date(sucursal.updated_at) <= endOfYesterday
    ).length;

    const percentageChange =
      totalYesterday === 0
        ? totalSucursales > 0
          ? 100
          : 0
        : ((totalSucursales - totalYesterday) / totalYesterday) * 100;

    return [formatNumber(totalSucursales, 0), percentageChange.toFixed(2)];
  } catch (error) {
    console.error("Error fetching sucursales:", error.message);
    return ["0", 0];
  }
};

export const getActivos = async (companyId) => {
  try {
    const [inventarioRes, almacenesRes] = await Promise.all([
      axios.get(`${endpoint}/inventario`),
      axios.get(`${endpoint}/almacenes`),
    ]);

    let inventario = Array.isArray(inventarioRes.data) ? inventarioRes.data : [];
    const almacenes = Array.isArray(almacenesRes.data) ? almacenesRes.data : [];

    // Filtrar inventario por empresa si hay un companyId
    if (companyId) {
      const almacenesIds = almacenes
        .filter((a) => String(a.empresa_id) === String(companyId))
        .map((a) => a.id);
      inventario = inventario.filter((inv) => almacenesIds.includes(inv.almacen_id));
    }

    let activos = 0;
    inventario.forEach((inv) => {
      activos += (Number(inv.precio) || 0) * (Number(inv.cantidad) || 0);
    });

    // Calcular el total de la semana pasada (sin mutar 'now')
    const now = new Date();
    const dayOfWeek = now.getDay();

    // Inicio de esta semana (Domingo pasado)
    const startOfThisWeek = new Date(now);
    startOfThisWeek.setDate(now.getDate() - dayOfWeek);
    startOfThisWeek.setHours(0, 0, 0, 0);

    // Inicio de la semana pasada
    const startOfLastWeek = new Date(startOfThisWeek);
    startOfLastWeek.setDate(startOfLastWeek.getDate() - 7);

    // Fin de la semana pasada (Sábado)
    const endOfLastWeek = new Date(startOfLastWeek);
    endOfLastWeek.setDate(endOfLastWeek.getDate() + 6);
    endOfLastWeek.setHours(23, 59, 59, 999);

    const totalLastWeek = inventario
      .filter((inv) => {
        if (!inv.updated_at) return false;
        const updatedAt = new Date(inv.updated_at);
        return updatedAt >= startOfLastWeek && updatedAt <= endOfLastWeek;
      })
      .reduce((acc, inv) => acc + (Number(inv.precio) || 0) * (Number(inv.cantidad) || 0), 0);

    const percentageChange =
      totalLastWeek === 0
        ? activos > 0
          ? 100
          : 0
        : ((activos - totalLastWeek) / totalLastWeek) * 100;

    return [formatCompactNumber(activos), percentageChange.toFixed(2)];
  } catch (error) {
    console.error("Error fetching activos:", error.message);
    return ["$0.00", 0];
  }
};
